import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { platformApi } from "../../api/platformApi";

const EXCLUDED_PREFIXES = ["/sign-in", "/display", "/not-authorized"];

/**
 * Converts a browser pathname into a privacy-safe route template (e.g. `/work-orders/600118%7C000` -> `/work-orders/:key`).
 * Query strings, fragments, and concrete identifiers are never sent to `/api/v1/usage/events`.
 */
export function toRouteTemplate(pathname: string): string | null {
  if (!pathname || !pathname.startsWith("/") || pathname.startsWith("//")) return null;
  const clean = pathname.split("?")[0]?.split("#")[0]?.trim() ?? "";
  if (!clean || clean === "/") return null;
  if (EXCLUDED_PREFIXES.some((prefix) => clean === prefix || clean.startsWith(`${prefix}/`))) return null;

  const segments = clean.split("/").filter(Boolean);
  if (segments.length === 0 || segments.length > 8) return null;

  if (segments[0] === "work-orders" && segments.length >= 2) {
    return "/work-orders/:key";
  }

  const normalized = segments.map((seg) => {
    if (seg === "as9100") return seg;
    if (seg.startsWith(":")) return seg;
    if (/\d/.test(seg) || seg.includes("%") || seg.includes("|") || seg.includes("@") || !/^[a-z][a-z-]*$/.test(seg)) {
      return ":id";
    }
    return seg;
  });

  return `/${normalized.join("/")}`;
}

/**
 * Posts the current route template on navigation for the aggregated Adoption dashboard.
 * Add `useUsageRecorder();` inside `AppShell`.
 */
export function useUsageRecorder(enabled = true): void {
  const { pathname } = useLocation();
  const lastRecordedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const template = toRouteTemplate(pathname);
    if (!template || template === lastRecordedRef.current) return;
    lastRecordedRef.current = template;
    void platformApi.recordUsageEvents([template]).catch(() => {
      // Usage telemetry never blocks or surfaces an error on navigation.
    });
  }, [enabled, pathname]);
}
